import React, { FC, forwardRef, ReactNode, ElementType, Fragment, useMemo, useState, Children } from 'react';
import {
  AppBar,
  AppBarProps,
  Tabs,
  TabsProps,
  Box,
  BoxProps,
  Toolbar,
  ToolbarProps,
  Typography,
  TypographyProps,
} from '@mui/material';

export interface SecondaryNavigationProps extends Omit<AppBarProps, 'color' | 'title'> {
  /**
   * Headline
   */
  title?: ReactNode;
  /**
   * Wrap the navigation into container
   * @default false
   */
  inContainer?: boolean;
  /**
   * ActionButtons on the right side
   */
  buttons?: ReactNode;
  /**
   * Properties applied to toolbar container
   */
  toolbarProps?: ToolbarProps<ElementType>;
  /**
   * Properties applied to title
   */
  titleProps?: TypographyProps<ElementType>;
  /**
   * Properties applied to content wrapper
   */
  contentProps?: TabsProps;
  /**
   * Properties applied to button wrapper
   */
  buttonsProps?: BoxProps<ElementType>;
}

/**
 * Additional navigation intended to be shown bellow the Header
 */
export const SecondaryNavigation: FC<SecondaryNavigationProps> = forwardRef(
  (
    {
      children,
      sx,
      title,
      buttons,
      inContainer = false,
      toolbarProps,
      titleProps,
      contentProps,
      buttonsProps,
      ...rest
    },
    ref,
  ) => {
    const { sx: titleSx, ...restTitle } = titleProps ?? {};
    const { sx: toolbarSx, ...restToolbar } = toolbarProps ?? {};
    const { sx: contentSx, ...restContent } = contentProps ?? {};
    const { sx: buttonsSx, ...restButtons } = buttonsProps ?? {};
    const [activeNav, setActiveNav] = useState(0);

    /**
     * Process children/navigationItems and remove any React.Fragments as Tabs requires direct elements
     */
    const navigation = useMemo(() => {
      function flatten(nodes) {
        const result = [];
        Children.forEach(nodes, (child) => {
          if (child?.type === Fragment) {
            result.push(...flatten(child.props.children));
          } else {
            result.push(child);
          }
        });
        return result;
      }
      return flatten(children);
    }, [children]);

    return (
      <AppBar
        sx={{
          // bellow Header, also defined in appbar.theme.ts
          zIndex: (theme) => theme.zIndex.appBar - 20,
          ...sx,
        }}
        ref={ref}
        data-testid="SecondaryNavigation"
        aria-label="Secondary Navigation"
        role="group"
        {...rest}
      >
        <Toolbar
          sx={{
            ...(!!navigation?.length && {
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              pt: { xs: 2, lg: 0 },
            }),
            ...toolbarSx,
          }}
          disableGutters={!inContainer}
          data-testid="SecondaryNavigation.wrapper"
          {...restToolbar}
        >
          {title && (
            <Typography
              data-testid="SecondaryNavigation.title"
              variant="subheadline"
              sx={{
                display: 'block',
                // on mobile push the button to the right side
                flexGrow: { xs: '1', lg: '0' },
                flexShrink: '0',
                ...titleSx,
              }}
              {...restTitle}
            >
              {title}
            </Typography>
          )}
          {!!navigation?.length && (
            <Tabs
              value={activeNav}
              onChange={(_, v) => setActiveNav(v)}
              template="navigation"
              sx={{
                flexGrow: '1',
                // on mobile move to 2nd row
                flexBasis: { xs: '100%', lg: 'auto' },
                order: { xs: '3', lg: '0' },
                // center on mobile
                justifyContent: { xs: 'center', lg: 'flex-start' },
                alignSelf: { xs: 'flex-end', lg: 'center' },
                ...(title && {
                  // increase size between title and nav
                  pl: { lg: 4 },
                }),
                marginTop: { xs: '-10px', lg: '0' },
                // move directly into border
                marginBottom: '-1px',
                ...contentSx,
              }}
              data-testid="SecondaryNavigation.items"
              {...restContent}
            >
              {navigation}
            </Tabs>
          )}
          {buttons && (
            <Box
              sx={{
                flexShrink: '0',
                flexGrow: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.25,
                ...buttonsSx,
              }}
              data-testid="SecondaryNavigation.cta"
              {...restButtons}
            >
              {buttons}
            </Box>
          )}
        </Toolbar>
      </AppBar>
    );
  },
);
